import { SelToolbarBtn } from "./EditorButtons";
import Icon from "./Icon";
import SelEditorPopup from "./SelEditorPopup";
import { clampAnchor, getMultiToolbarAnchor, getToolbarAnchor } from "./selection";
import { HANDLE_TO_MAG_KEY, getFirstEmbeddedMagnifier } from "./drawObjects";

// ─── Zoom slider shown when the selected object has at least one magnifier (standalone or embedded)
function EmbeddedMagnifierSlider({ obj, imgSize, onUpdateObj }) {
  const isStandalone = obj.type === "magnifier";
  const mag = isStandalone ? obj : getFirstEmbeddedMagnifier(obj);
  if (!mag) return null;

  // Position the slider below the bottom of the shape / lens area
  const allY = [obj.y1, obj.y2];
  if (isStandalone) {
    const r = obj.lensRadius || 70;
    allY.push(obj.lensY + r);
    if (obj.targetY !== undefined) allY.push(obj.targetY);
    if (obj.anchorY !== undefined) allY.push(obj.anchorY);
  } else {
    Object.values(HANDLE_TO_MAG_KEY).forEach(k => {
      if (obj[k]) { const r = obj[k].lensRadius || 70; allY.push(obj[k].lensY + r); }
    });
  }
  const bottomY = Math.max(...allY.filter(y => y !== undefined && !isNaN(y)));
  const sliderTop = Math.min(bottomY + 18, imgSize.h - 54);
  const centerX = isStandalone ? obj.lensX : (obj.x1 + obj.x2) / 2;

  return (
    <div
      style={{
        position: "absolute",
        left: `${(centerX / imgSize.w) * 100}%`,
        top: `${(sliderTop / imgSize.h) * 100}%`,
        transform: "translateX(-50%)",
        zIndex: 52,
        background: "rgba(20,20,22,0.94)",
        borderRadius: 10,
        padding: "7px 10px",
        boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: "#fff",
        fontSize: 12,
        pointerEvents: "auto",
      }}
      onMouseDown={e => e.stopPropagation()}
      onTouchStart={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
    >
      <span style={{ whiteSpace: "nowrap" }}>Zoom {Number(mag.zoomLevel || 3).toFixed(1)}x</span>
      <input
        className="fd-dark-slider"
        type="range"
        min={1.5}
        max={5}
        step={0.1}
        value={mag.zoomLevel || 3}
        onChange={e => {
          const zoomLevel = Number(e.target.value);
          if (isStandalone) {
            onUpdateObj({ zoomLevel });
          } else {
            // Apply the new zoom to ALL embedded magnifiers on this object
            const patch = {};
            Object.values(HANDLE_TO_MAG_KEY).forEach(k => {
              if (obj[k]) patch[k] = { ...obj[k], zoomLevel };
            });
            onUpdateObj(patch);
          }
        }}
        style={{ width: 140, accentColor: obj.color || "#ffcc00" }}
      />
    </div>
  );
}

export default function SelectionOverlay({
  objects,
  selectedIds,
  imgSize,
  showCrop,
  tool,
  showSelEditor,
  editSelected,
  duplicateObjects,
  deleteSelected,
  updateSelectedObj,
  cancelStyleEdit,
  doneStyleEdit,
}) {
  if (!selectedIds.length || showCrop || tool !== "cursor") return null;

  const sels = objects.filter(o => selectedIds.includes(o.id));
  if (!sels.length) return null;

  const rawAnchor = sels.length === 1 ? getToolbarAnchor(sels[0]) : getMultiToolbarAnchor(sels);
  const anchor = clampAnchor(rawAnchor, imgSize);

  const singleSel = sels.length === 1 ? sels[0] : null;
  const showMagSlider = singleSel && (singleSel.type === "magnifier" || getFirstEmbeddedMagnifier(singleSel));

  return (
    <>
      {/* Zoom slider for active magnifiers */}
      {showMagSlider && (
        <EmbeddedMagnifierSlider
          obj={singleSel}
          imgSize={imgSize}
          onUpdateObj={patch => updateSelectedObj(singleSel.id, patch)}
        />
      )}

      {/* Copy / Delete / Edit-style toolbar */}
      <div
        style={{ position: "absolute", left: `${(anchor.x / imgSize.w) * 100}%`, top: `${(anchor.y / imgSize.h) * 100}%`, transform: "translateX(-50%)", display: "flex", gap: 4, background: "rgba(20,20,22,0.94)", borderRadius: 9, padding: 4, boxShadow: "0 6px 20px rgba(0,0,0,0.4)", zIndex: 50 }}
        onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
        {sels.length === 1 && sels[0].type !== "pen" && sels[0].type !== "blur" && (
          <SelToolbarBtn title={sels[0].type === "text" || sels[0].type === "timestamp" ? "Edit text" : "Edit style"} onClick={editSelected}><Icon id="text" size={14}/></SelToolbarBtn>
        )}
        <SelToolbarBtn title="Copy" onClick={() => duplicateObjects(selectedIds)}><Icon id="copy" size={14}/></SelToolbarBtn>
        <SelToolbarBtn title="Delete" onClick={deleteSelected}><Icon id="trash" size={14}/></SelToolbarBtn>
      </div>
      {showSelEditor && sels.length === 1 && (
        <SelEditorPopup
          obj={sels[0]}
          onUpdate={patch => updateSelectedObj(sels[0].id, patch)}
          onCancel={cancelStyleEdit}
          onDone={doneStyleEdit}
        />
      )}
    </>
  );
}
